import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { KeyRound, ArrowLeft, Mail, CheckCircle2 } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Card } from '../components/ui/card';
import { toast } from 'sonner';
import axios from 'axios';
import logo from '../assets/logo-asistelo.png';

const API_URL = process.env.REACT_APP_BACKEND_URL;

export const ForgotPassword = () => {
  const navigate = useNavigate();
  const [email, setEmail] = useState('');
  const [loading, setLoading] = useState(false);
  const [emailSent, setEmailSent] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!email) {
      toast.error('Por favor ingresa tu email');
      return;
    }

    try {
      setLoading(true);
      await axios.post(`${API_URL}/api/auth/forgot-password`, { email });
      setEmailSent(true);
      toast.success('Instrucciones enviadas a tu email');
    } catch (error) {
      // Siempre mostrar mensaje genérico por seguridad
      setEmailSent(true);
    } finally {
      setLoading(false);
    }
  };

  if (emailSent) {
    return (
      <div className="min-h-screen bg-white flex items-center justify-center p-4">
        <Card className="max-w-md w-full p-8 shadow-lg border border-slate-200">
          {/* Logo */}
          <div className="flex justify-center mb-6">
            <img src={logo} alt="Asistelo" className="h-12 w-auto" />
          </div>

          {/* Success Icon */}
          <div className="flex justify-center mb-6">
            <div className="w-20 h-20 bg-green-100 rounded-full flex items-center justify-center">
              <CheckCircle2 className="w-10 h-10 text-green-600" />
            </div>
          </div>

          <h1 className="text-2xl font-bold text-slate-900 mb-3 text-center" style={{ fontFamily: 'Plus Jakarta Sans, sans-serif' }}>
            ¡Email Enviado!
          </h1>

          <p className="text-slate-600 mb-6 text-center" style={{ fontFamily: 'Inter, sans-serif' }}>
            Si el email <strong>{email}</strong> está registrado en nuestro sistema, 
            recibirás instrucciones para restablecer tu contraseña.
          </p>

          <div className="bg-amber-50 border border-amber-200 rounded-lg p-4 mb-6">
            <p className="text-sm font-semibold text-amber-900 mb-2">
              ⏰ Importante:
            </p>
            <ul className="text-sm text-amber-800 space-y-2">
              <li className="flex items-start">
                <span className="mr-2">•</span>
                <span>Revisa tu bandeja de entrada y spam</span>
              </li>
              <li className="flex items-start">
                <span className="mr-2">•</span>
                <span>El enlace expira en <strong>15 minutos</strong> por seguridad</span>
              </li>
              <li className="flex items-start">
                <span className="mr-2">•</span>
                <span>Si no recibes el email, verifica que el correo sea correcto</span>
              </li>
            </ul>
          </div>

          <Button
            onClick={() => navigate('/login')}
            className="w-full bg-[#2537b0] hover:bg-[#1c2a8a]"
          >
            Volver al Login
          </Button>
        </Card>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-white flex items-center justify-center p-4">
      <Card className="max-w-md w-full p-8 shadow-lg border border-slate-200">
        {/* Logo */}
        <div className="flex justify-center mb-6">
          <img src={logo} alt="Asistelo" className="h-12 w-auto" />
        </div>

        {/* Icon */}
        <div className="flex justify-center mb-6">
          <div className="w-20 h-20 bg-[#2537b0]/10 rounded-full flex items-center justify-center">
            <KeyRound className="w-10 h-10 text-[#2537b0]" />
          </div>
        </div>

        <h1 className="text-2xl font-bold text-slate-900 mb-3 text-center" style={{ fontFamily: 'Plus Jakarta Sans, sans-serif' }}>
          ¿Olvidaste tu Contraseña?
        </h1>

        <p className="text-slate-600 text-center mb-6" style={{ fontFamily: 'Inter, sans-serif' }}>
          No te preocupes, te enviaremos instrucciones para restablecerla.
        </p>

        <form onSubmit={handleSubmit} className="space-y-5">
          <div>
            <Label htmlFor="email" className="text-xs font-semibold uppercase tracking-wider text-slate-500">
              Correo Electrónico
            </Label>
            <div className="relative mt-2">
              <Mail className="absolute left-3 top-1/2 transform -translate-y-1/2 text-slate-400 w-5 h-5" />
              <Input
                id="email"
                type="email"
                placeholder="tu@email.com"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                className="pl-10"
                required
              />
            </div>
          </div>

          <Button
            type="submit"
            disabled={loading}
            className="w-full bg-[#2537b0] hover:bg-[#1c2a8a] py-3 font-medium"
          >
            {loading ? (
              <>
                <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                Enviando...
              </>
            ) : (
              'Enviar Instrucciones'
            )}
          </Button>
        </form>

        <div className="mt-6 pt-6 border-t border-slate-200">
          <Button
            onClick={() => navigate('/login')}
            variant="ghost"
            className="w-full text-slate-600 hover:text-slate-900"
          >
            <ArrowLeft className="w-4 h-4 mr-2" />
            Volver al Login
          </Button>
        </div>

        <div className="mt-6 bg-blue-50 border border-blue-200 rounded-lg p-4">
          <p className="text-xs text-blue-900 text-center">
            <strong>🔒 Nota de seguridad:</strong> Por tu protección, no revelaremos 
            si un email existe en nuestro sistema.
          </p>
        </div>
      </Card>
    </div>
  );
};
